import React from 'react';
import { TriangleAlert } from 'lucide-react';
import { Button, EmptyState } from '@/components/ds-dlp';

interface ErrorBoundaryProps {
  /** Override the default fallback UI. Receives the caught error and a reset fn. */
  fallback?: (error: Error, reset: () => void) => React.ReactNode;
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Catches render-time exceptions in a subtree and shows a ds EmptyState with
 * a retry (cascade plan 5.3). App mounts one around each page, keyed by the
 * path, so one broken page no longer blanks the shell, and navigating away
 * starts afresh.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught', error, info);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback(this.state.error, this.reset);
      return (
        <div className="flex min-h-[60vh] items-center justify-center">
          <EmptyState
            icon={<TriangleAlert />}
            title="This page couldn't be shown"
            description={
              <>
                Something went wrong while drawing it. Try again; if it keeps happening, send this to your admin:{' '}
                <code className="font-mono text-dense">{this.state.error.message}</code>
              </>
            }
            action={<Button onClick={this.reset}>Try again</Button>}
            headingLevel={2}
          />
        </div>
      );
    }
    return this.props.children;
  }
}
